import React, { ReactElement, useEffect, useState } from 'react';
import { ProductSearch } from 'components/amm-product-search/ProductSearch';
import { CategoriesBreadCrumb } from 'components/categories-breadcrumb/CategoriesBreadCrumb';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { SliderValueType } from 'primereact/slider';
import {
  CartItemInput,
  CartItemInterface,
  ProductInterface,
  useAddProductsToCartMutation,
  ProductFieldsFragment,
} from 'graphql/generated/magentoApi';
import { DataView, DataViewPageParams } from 'primereact/dataview';
import { Head } from 'components/head/Head';
import {
  useSearchParams,
  useNavigate,
  To,
  NavigateOptions,
} from 'react-router-dom';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Sidebar } from 'primereact/sidebar';
import { useCartContext } from 'CartContext';
import {
  useGetProducts,
  useGetProductsByCategoryPath,
  useGetProductsBySkus,
} from 'hooks/magentoHooks';
// import { useMarketplace } from 'hooks/useMarketplace';
import ColorParam from 'components/ColorParam';
import Footer from 'components/footer-crwd/Footer';
import Product from 'components/Product';
import ReactGA from 'react-ga4';
import useViewport from 'hooks/useViewport';
import FocusLock from 'react-focus-lock';
import { ProductFilter } from 'components/product-filter/ProductFilter';
import ShopNavBar from './shop-home-page/shop-nav-bar';
import ProductCard from './ProductCard';
import RightSidebarCart from '../components/right-sidebar-cart/RightSidebarCart';
import './ProductList.scss';

export const rootPath = 'shopping';

function buildSkusArray(skusParam: string | undefined): string[] {
  if (skusParam) {
    return [...skusParam.split(',')];
  }
  return [];
}

interface companyVendors {
  name?: string;
}

export default function ProductList(): ReactElement {
  const navigateBase = useNavigate();
  // const marketplace = useMarketplace();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') || '1';
  const perPageParam = searchParams.get('per-page');
  //! temporarily set perPageParam to 300 due to a pagination bug
  const perPage = Number(perPageParam || 300);
  const openExternal = searchParams.get('open-external') === 'true';
  const hideCategories = searchParams.get('hide-categories') === 'true';
  const vendorParam = searchParams.get('vendor') || undefined;
  const skusParam = searchParams.get('skus') || undefined;
  const { products: skuProducts } = useGetProductsBySkus({
    skus: buildSkusArray(skusParam),
  });
  const [currentPage, setCurrentPage] = useState<number>(Number(page) || 1);
  const [first, setFirst] = useState<number>(perPage * (currentPage - 1));
  const [menuModel, setMenuModel] = useState<MenuItem[]>([
    { label: 'Shop Categories', items: [] },
  ]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [visibleRight, setVisibleRight] = useState(false);
  const [categoryPaths, setCategoryPaths] = useState<string[]>([
    'shopping/health',
  ]);

  const {
    products,
    totalProducts,
    fetching: fetchingProducts,
  } = useGetProductsByCategoryPath({
    page: currentPage,
    categoryPaths,
    perPage,
    skuProducts,
  });

  const [product, setProduct] = useState<
    Array<ProductFieldsFragment | null> | undefined
  >(products || undefined);

  useEffect(() => {
    if (vendorParam && product) {
      const vendorCategories = product[0]?.categories
        .filter(item => item.url_path.toLowerCase().includes('shopping'))
        .filter(item => item.url_path !== 'shopping')
        .map(category => category.url_path.toString());

      setCategoryPaths(vendorCategories as string[]);
    }
  }, [vendorParam, product]);

  const filteredProducts: ProductFieldsFragment[] = products.filter(
    item => item !== null,
  ) as ProductFieldsFragment[];
  // * get maximum price/highest priced product to use as a max value for slider:
  const maxProductPrice =
    filteredProducts.sort(
      (a, b) =>
        (b.price_range.maximum_price?.final_price.value || 0) -
        (a.price_range.maximum_price?.final_price.value || 0),
    )[0]?.price_range.maximum_price?.final_price.value || 0;

  const [priceRange, setPriceRange] = useState<SliderValueType>([
    0,
    maxProductPrice,
  ]);
  const [selectedVendors, setSelectedVendors] =
    useState<Array<companyVendors> | null>(null);

  // useEffect(() => {
  //   if (marketplace?.categories) {
  //     navigateBase(`/?categories=${marketplace?.categories}`);
  //   }
  // }, []);

  const [loading, setLoading] = useState(false);
  const [productSku, setCurrentProductSku] = useState<string>('');
  const [addProductState, addProducts] = useAddProductsToCartMutation();
  const { cartId, updateItems, reset } = useCartContext();
  const [searchProductAuto, setSearchProductAuto] =
    useState<ProductInterface | null>();
  const [filteredProduct, setFilteredProduct] = useState<
    (ProductFieldsFragment | null)[]
  >([]);
  const { products: allProducts } = useGetProducts();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const Link = (props: LinkProps & React.RefAttributes<HTMLAnchorElement>) => (
  //   <LinkBase {...props} target={openExternal ? '_blank' : undefined} />
  // );

  const navigate = (to: To, options?: NavigateOptions) => {
    if (openExternal) {
      window.open(to as string, '_blank');
    } else {
      navigateBase(to, options);
    }
  };

  const navigateToPage = (pageNumber: number) => {
    if (pageNumber > 1) {
      searchParams.set('page', `${pageNumber}`);
    } else {
      searchParams.delete('page');
    }
    setSearchParams(searchParams);
  };

  useEffect(() => {
    const goToFirstPage =
      document.getElementsByClassName('p-paginator-first')[0];
    if (goToFirstPage) {
      goToFirstPage.classList.remove('p-disabled');
      goToFirstPage.removeAttribute('disabled');
      goToFirstPage.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        navigateToPage(1);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    setFirst(perPage * (Number(page) - 1));
    setCurrentPage(Number(page));
  }, [page, perPage]);

  useEffect(() => {
    const items = addProductState.data?.addProductsToCart?.cart.items as
      | CartItemInterface[]
      | undefined;
    if (!addProductState.fetching && items?.length) {
      updateItems([...items]);
      setLoading(false);
      setVisible(true);
    } else if (addProductState.error) {
      setLoading(false);
      reset();
    }
  }, [addProductState, reset, updateItems]);

  const onClickAddBtn = (sku: string) => {
    if (openExternal) {
      navigate(`/store/cart?add-sku=${sku}`);
      return;
    }
    setVisibleRight(true);
    setLoading(true);
    setCurrentProductSku(sku);
    addProducts({
      cartId,
      cartItems: [
        {
          sku,
          quantity: 1,
        },
      ] as CartItemInput[],
    });
    ReactGA.event({
      category: 'ecommerce',
      action: 'add_to_cart',
      label: `Product Card Action ${sku}`,
    });
  };

  const itemTemplate = (product: ProductInterface) => (
    <ProductCard
      product={product}
      onClickAddBtn={onClickAddBtn}
      loading={loading && product.sku === productSku}
    />
  );

  const onPageChange = ({ page, first }: DataViewPageParams) => {
    setFirst(first);
    setCurrentPage(page + 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    navigateToPage(page + 1);
  };

  useEffect(() => {
    let filterProducts: Array<ProductFieldsFragment> = [...products].filter(
      item => item !== null,
    ) as ProductFieldsFragment[];
    if (!fetchingProducts) {
      filterProducts = filterProducts.filter(
        item =>
          (item?.price_range.maximum_price?.final_price?.value || 0) >=
            priceRange[0] &&
          (item?.price_range.maximum_price?.final_price?.value || 0) <=
            priceRange[1],
      );

      if (selectedVendors) {
        filterProducts = filterProducts.filter(item =>
          selectedVendors.some(
            vendorItem => item?.vendor_company === vendorItem.name,
          ),
        );
      }
      if (JSON.stringify(filterProducts) !== JSON.stringify(product)) {
        setProduct(filterProducts);
      }
    }
  }, [fetchingProducts, priceRange, selectedVendors, products, product]);

  // to fix edge case with price range filter when user selects a category:
  useEffect(() => {
    vendorParam ? setSelectedVendors([{ name: vendorParam }]) : clearFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryPaths, vendorParam]);

  const clearFilters = () => {
    setPriceRange([0, maxProductPrice]);
    setSelectedVendors(null);
  };

  const shouldShowAside =
    !hideCategories &&
    !searchProductAuto?.sku &&
    (!!menuModel[0].items || loadingCategories);

  useEffect(() => {
    if (!shouldShowAside) {
      clearFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowAside]);

  const { width } = useViewport();
  const isMobileWidth = width < 992;
  const [isMobile, setIsMobile] = useState(isMobileWidth);

  useEffect(() => {
    if (isMobileWidth !== isMobile) {
      setIsMobile(isMobileWidth);
    }
  }, [width, isMobile, isMobileWidth]);

  if (!categoryPaths) {
    return <></>;
  }

  return (
    <>
      <Head
        title={
          categoryPaths[0]
            ? `${categoryPaths[0]
                ?.split('-')
                ?.join(' ')
                ?.replace(/\b\b\w/g, (c: string) => c.toUpperCase())
                ?.split('/')
                .join(', ')}`
            : 'Blockchain Store'
        }
        link="/category?categories=shopping/beauty,shopping/health"
        twitterUsername="@advancedmedmkt"
      />
      <ColorParam />
      <ShopNavBar />

      <Sidebar
        className="w-1/5 sm:w-auto cart-summary__sidebar"
        visible={visibleRight}
        position="right"
        closeOnEscape
        showCloseIcon={false}
        onHide={() => setVisibleRight(false)}
      >
        <RightSidebarCart setVisibleRight={setVisibleRight} />
      </Sidebar>

      <div
        className="categories grid mb-8"
        style={{ maxWidth: '1140px', margin: '0 auto' }}
      >
        {!hideCategories && (
          <div className="flex justify-content-start align-items-center w-full mb-2 md:mb-3 flex-column md:flex-row">
            {!searchProductAuto?.sku && (
              <>
                <CategoriesBreadCrumb
                  componentStyle="category-page__breadcrumb border-none col-12 md:col-6 p-2 my-3 md:mb-0 w-full"
                  setLoadingCategories={setLoadingCategories}
                  setMenuModel={setMenuModel}
                  setCategoryPaths={setCategoryPaths}
                  categoryPaths={categoryPaths}
                />
              </>
            )}
            <div className="relative col-12 md:col-6 hidden">
              <ProductSearch
                categoryPaths={categoryPaths}
                allProducts={allProducts}
                product={product}
                searchSuggestions={filteredProduct}
                setFilteredProduct={setFilteredProduct}
                setSearchProductAuto={setSearchProductAuto}
              />
              {searchProductAuto?.sku && (
                <button
                  type="button"
                  onClick={() => setProduct(undefined)}
                  className="bg-transparent border-transparent flex align-items-center cursor-pointer absolute p-2 border-none"
                  style={{
                    top: '0.5rem',
                    right: '0.5rem',
                    height: 'calc(100% - 1rem)',
                  }}
                >
                  <i className="pi pi-times" />
                </button>
              )}
            </div>
          </div>
        )}
        {shouldShowAside && (
          <>
            {isMobile ? (
              <div className="mb-4">
                <Sidebar
                  visible={visible}
                  onHide={() => setVisible(false)}
                  className="sidebar-filter"
                >
                  <FocusLock>
                    <aside className="col-10 sm:col-12 lg:col-3 px-1 sm:px-3 lg:px-2">
                      {!loadingCategories && categoryPaths ? (
                        <>
                          <h3 className="uppercase mb-5">Product Filters</h3>
                          {typeof menuModel[0].items === 'object' &&
                          menuModel[0].items.length !== 0 ? (
                            <Menu
                              model={menuModel}
                              className="w-full shop-categories p-card p-0 mb-3"
                            />
                          ) : (
                            <CategoriesBreadCrumb
                              componentStyle="w-full p-breadcrumb-chevron--hidden p-breadcrumb-category mb-4"
                              setLoadingCategories={setLoadingCategories}
                              setMenuModel={setMenuModel}
                              setCategoryPaths={setCategoryPaths}
                              categoryPaths={categoryPaths}
                              showHome
                            />
                          )}
                        </>
                      ) : (
                        <ProgressSpinner
                          strokeWidth="5"
                          animationDuration=".5s"
                          style={{
                            width: '50px',
                            height: '50px',
                            margin: ' 0 25%',
                          }}
                        />
                      )}
                      <ProductFilter
                        setVisible={setVisible}
                        setSelectedVendors={setSelectedVendors}
                        selectedVendors={selectedVendors}
                        shouldShowAside={shouldShowAside}
                        isMobile={isMobile}
                        products={products}
                        priceRange={priceRange}
                        setPriceRange={setPriceRange}
                        maxProductPrice={maxProductPrice}
                      />
                    </aside>
                  </FocusLock>
                </Sidebar>
                <Button
                  icon="pi pi-filter"
                  label="Filter Products"
                  className="shop-button green solid ml-2 my-0 py-2 px-2 text-xs flex align-items-center flex-row"
                  onClick={() => setVisible(true)}
                />
              </div>
            ) : (
              <aside className="col-12 lg:col-3 px-3 lg:px-2 cat-aside">
                {menuModel.length ? (
                  <>
                    {typeof menuModel[0].items === 'object' &&
                    menuModel[0].items.length !== 0 ? (
                      <>
                        {/* <CategoriesBreadCrumb
                          componentStyle="w-full p-breadcrumb-chevron--hidden p-breadcrumb-category mb-4"
                          setLoadingCategories={setLoadingCategories}
                          setMenuModel={setMenuModel}
                          setCategoryPaths={setCategoryPaths}
                          categoryPaths={categoryPaths}
                          showHome
                        /> */}

                        <Menu
                          model={menuModel}
                          className="w-full shop-categories p-card p-0 mb-3"
                        />
                      </>
                    ) : (
                      <>
                        <CategoriesBreadCrumb
                          componentStyle="w-full p-breadcrumb-chevron--hidden p-breadcrumb-category mb-4"
                          setLoadingCategories={setLoadingCategories}
                          setMenuModel={setMenuModel}
                          setCategoryPaths={setCategoryPaths}
                          categoryPaths={categoryPaths}
                          showHome
                        />
                      </>
                    )}
                    <ProductFilter
                      setVisible={setVisible}
                      setSelectedVendors={setSelectedVendors}
                      selectedVendors={selectedVendors}
                      shouldShowAside={shouldShowAside}
                      isMobile={isMobile}
                      products={products}
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      maxProductPrice={maxProductPrice}
                    />
                  </>
                ) : (
                  <ProgressSpinner
                    strokeWidth="5"
                    animationDuration=".5s"
                    style={{ width: '50px', height: '50px', margin: ' 0 25%' }}
                  />
                )}
              </aside>
            )}
          </>
        )}
        {searchProductAuto?.sku ? (
          <Product sku={searchProductAuto.sku} classNames="col-12" />
        ) : (
          <DataView
            paginator
            rows={perPage}
            first={first}
            totalRecords={totalProducts}
            onPage={e => onPageChange(e)}
            value={product}
            layout="grid"
            itemTemplate={itemTemplate}
            className={`max-w-full col-12  scalein p-0 ${
              shouldShowAside ? 'lg:col-9' : ''
            }`}
          />
        )}
      </div>
      <Footer />
    </>
  );
}
